"use client"

import { useEffect, useMemo } from "react"
import { nativeEventBridge, EventTypeMap } from "@/utils/nativeEventBridge"
import { useShelfAnalysisHandler } from "@/hooks/useShelfAnalysisHandler"
import { nativeBr } from "@/utils/nativeBr"
import { useAppVersionStore } from "@/hooks/common/store/useAppVersionStore"

// 모든 네이티브 이벤트 핸들러를 관리하는 커스텀 훅
const useNativeEventHandlers = () => {
  const { handleAnalysisComplete } = useShelfAnalysisHandler()
  
  // 확장 가능한 이벤트 핸들러 매핑
  const eventHandlers = useMemo(() => ({
    "shelf-photo-analysis-complete": handleAnalysisComplete,
    // 향후 추가될 이벤트들...
    // "user-profile-update": useUserProfileHandler().handleProfileUpdate,
    // "notification-received": useNotificationHandler().handleNotification,
  }), [handleAnalysisComplete])
  
  return eventHandlers
}

// 확장 가능한 네이티브 이벤트 브리지 초기화 컴포넌트
const NativeEventBridgeInitializer = () => {
  const eventHandlers = useNativeEventHandlers()
  const { setAppVersion } = useAppVersionStore()

  useEffect(() => {
    // 앱 버전 초기화
    const initAppVersion = async () => {
      if (!nativeBr.isEnabled) return

      try {
        const { version, osType } = await nativeBr.appVersion()
        setAppVersion(version, osType)
        if (process.env.NODE_ENV === "development") {
          console.log(`[AppVersion] Initialized:`, { version, osType })
        }
      } catch (e) {
        console.error("[AppVersion] Failed to initialize:", e)
      }
    }
    void initAppVersion()

    // 등록된 핸들러들을 추적하기 위한 맵
    const registeredHandlers = new Map<keyof EventTypeMap, boolean>()
    
    // 모든 이벤트 핸들러를 동적으로 등록
    Object.entries(eventHandlers).forEach(([eventType, handler]) => {
      const typedEventType = eventType as keyof EventTypeMap
      const wasRegistered = nativeEventBridge.addEventListener(typedEventType, handler)
      registeredHandlers.set(typedEventType, wasRegistered)
      
      if (process.env.NODE_ENV === "development") {
        if (wasRegistered) {
          console.log(`[NativeEventBridge] 리스너 등록: ${eventType}`)
        } else {
          console.log(`[NativeEventBridge] 리스너 이미 등록됨: ${eventType}`)
        }
      }
    })

    // 개발환경에서 등록된 이벤트 목록 출력
    if (process.env.NODE_ENV === "development") {
      const registeredEvents = Array.from(registeredHandlers.keys())
      const newlyRegistered = Array.from(registeredHandlers.entries())
        .filter(([, wasRegistered]) => wasRegistered)
        .map(([eventType]) => eventType)
      
      console.log(`[NativeEventBridge] 초기화 완료`, {
        총이벤트수: registeredEvents.length,
        등록된이벤트: newlyRegistered,
        전체이벤트목록: registeredEvents,
      })
    }

    return () => {
      // 실제로 등록된 핸들러들만 해제
      Object.entries(eventHandlers).forEach(([eventType, handler]) => {
        const typedEventType = eventType as keyof EventTypeMap
        const wasRegistered = registeredHandlers.get(typedEventType)
        
        if (wasRegistered) {
          const removed = nativeEventBridge.removeEventListener(typedEventType, handler)
          
          if (process.env.NODE_ENV === "development" && removed) {
            console.log(`[NativeEventBridge] 리스너 해제: ${eventType}`)
          }
        }
      })
    }
  }, [eventHandlers])

  return null
}

export default NativeEventBridgeInitializer
